import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { FeedbackReportItem } from '../Models/FeedbackReportItem';
import { User } from '../Models/User';
import { UserService } from '../services/user.service';

interface FeedbackProbateGroup {
  probateId: number;
  deceasedName: string;
  dataCompleteDate: string | null;
  numberOfFeedbackItems: number;
  reviewers: string;
  dataApprovalUser: string;
  items: FeedbackReportItem[];
}

type SortColumn = 'probateId' | 'deceasedName' | 'dataCompleteDate' | 'numberOfFeedbackItems' | 'reviewers' | 'dataApprovalUser';

@Component({
  selector: 'app-feedback-report',
  templateUrl: './feedback-report.component.html',
  styleUrls: ['./feedback-report.component.css'],
  standalone: false
})
export class FeedbackReportComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  isSupervisor = false;
  loggedInUserId = 0;
  private loggedInUserTag = '';

  allRecords: FeedbackReportItem[] = [];
  allGroups: FeedbackProbateGroup[] = [];
  filteredGroups: FeedbackProbateGroup[] = [];
  pagedGroups: FeedbackProbateGroup[] = [];

  // Custom expand/collapse instead of mat-expansion-panel - Material's internal flex layout for
  // mat-panel-title made it impossible to guarantee this row lines up pixel-for-pixel with the
  // plain header bar above it, so this is a plain div using the exact same grid class instead.
  expandedProbateIds = new Set<number>();

  loading = false;

  pageSize = 25;
  pageSizeOptions = [10, 25, 50, 100];

  sortColumn: SortColumn = 'dataCompleteDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Client-side filters
  probateIdSearch = '';
  deceasedNameSearch = '';
  reviewerSearch = '';

  // Server-side filters (trigger a reload)
  dataCompleteUserId: number | null = null;
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  // Supervisor-only: full user list to populate the "Data Complete User" filter, independent of
  // whatever the current filtered/scoped result set happens to contain.
  allUsers: User[] = [];

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const user = this.userService.loggedInUser;
    this.loggedInUserId = user?.id || 0;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.isSupervisor = user?.roles?.some(r => r.id === 2) || false;

    if (this.isSupervisor) {
      this.loadAllUsers();
      // Supervisors must explicitly pick a Data Complete User before anything loads - showing
      // everyone's feedback at once by default returns too many rows and gives no per-row
      // indication of whose feedback each entry actually is.
      return;
    }

    this.loadData();
  }

  private loadAllUsers(): void {
    let params = new HttpParams().set('User', this.loggedInUserTag);
    this.http.get<User[]>('/charitycentral/GetAllUsers', { params }).subscribe({
      next: (users) => {
        this.allUsers = (users || []).slice().sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
      },
      error: (err) => console.error('Error loading users:', err)
    });
  }

  async loadData(): Promise<void> {
    if (this.isSupervisor && !this.dataCompleteUserId) {
      // No user selected yet - don't fetch the unbounded "everyone" set.
      this.allRecords = [];
      this.allGroups = [];
      this.filteredGroups = [];
      this.pagedGroups = [];
      return;
    }

    this.loading = true;
    try {
      let params = new HttpParams().set('userId', this.loggedInUserId);
      if (this.isSupervisor && this.dataCompleteUserId) {
        params = params.set('dataCompleteUserId', this.dataCompleteUserId);
      }
      if (this.dateFrom) params = params.set('dateFrom', this.toIsoDate(this.dateFrom));
      if (this.dateTo) params = params.set('dateTo', this.toIsoDate(this.dateTo));

      this.allRecords = await lastValueFrom(
        this.http.get<FeedbackReportItem[]>('/charitycentral/GetFeedbackReport', { params })
      );

      this.buildGroups();
      this.applyFilters();
    } catch {
      this.toastr.error('Failed to load feedback.', '', { timeOut: 4000 });
    } finally {
      this.loading = false;
    }
  }

  private buildGroups(): void {
    const byProbate = new Map<number, FeedbackReportItem[]>();
    for (const item of this.allRecords) {
      const list = byProbate.get(item.probateId);
      if (list) {
        list.push(item);
      } else {
        byProbate.set(item.probateId, [item]);
      }
    }

    this.allGroups = Array.from(byProbate.entries()).map(([probateId, items]) => {
      const first = items[0];
      const reviewerNames = Array.from(new Set(items.map(i => this.userDisplayName(i.feedbackUser))));
      return {
        probateId,
        deceasedName: first.deceasedName || '(unknown)',
        dataCompleteDate: first.dataCompleteDate,
        numberOfFeedbackItems: items.length,
        reviewers: reviewerNames.join(', '),
        dataApprovalUser: first.dataApprovalUser ? this.userDisplayName(first.dataApprovalUser) : '-',
        items
      };
    });
  }

  private userDisplayName(user: User | null): string {
    if (!user) return 'Unknown';
    return `${user.firstName} ${user.lastName}`.trim();
  }

  applyFilters(): void {
    const probateTerm = this.probateIdSearch.trim();
    const nameTerm = this.deceasedNameSearch.trim().toLowerCase();
    const reviewerTerm = this.reviewerSearch.trim().toLowerCase();

    this.filteredGroups = this.allGroups.filter(g => {
      const matchesProbateId = !probateTerm || g.probateId.toString().includes(probateTerm);
      const matchesName = !nameTerm || g.deceasedName.toLowerCase().includes(nameTerm);
      const matchesReviewer = !reviewerTerm || g.reviewers.toLowerCase().includes(reviewerTerm);
      return matchesProbateId && matchesName && matchesReviewer;
    });

    this.sortGroups();
    if (this.paginator) this.paginator.firstPage();
    this.updatePagedGroups();
  }

  clearFilters(): void {
    this.probateIdSearch = '';
    this.deceasedNameSearch = '';
    this.reviewerSearch = '';
    this.applyFilters();
  }

  clearServerFilters(): void {
    this.dataCompleteUserId = null;
    this.dateFrom = null;
    this.dateTo = null;
    this.loadData();
  }

  sortBy(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = column === 'dataCompleteDate' ? 'desc' : 'asc';
    }
    this.sortGroups();
    this.updatePagedGroups();
  }

  private sortGroups(): void {
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    const col = this.sortColumn;

    this.filteredGroups.sort((a, b) => {
      let av: string | number = a[col] ?? '';
      let bv: string | number = b[col] ?? '';

      if (col === 'dataCompleteDate') {
        av = a.dataCompleteDate ? new Date(a.dataCompleteDate).getTime() : 0;
        bv = b.dataCompleteDate ? new Date(b.dataCompleteDate).getTime() : 0;
      } else if (typeof av === 'string') {
        av = av.toLowerCase();
        bv = (bv as string || '').toLowerCase();
      }

      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }

  private updatePagedGroups(): void {
    const pageIndex = this.paginator?.pageIndex || 0;
    const start = pageIndex * this.pageSize;
    this.pagedGroups = this.filteredGroups.slice(start, start + this.pageSize);
  }

  pageChanged(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.updatePagedGroups();
  }

  toggleGroup(probateId: number): void {
    if (this.expandedProbateIds.has(probateId)) {
      this.expandedProbateIds.delete(probateId);
    } else {
      this.expandedProbateIds.add(probateId);
    }
  }

  isExpanded(probateId: number): boolean {
    return this.expandedProbateIds.has(probateId);
  }

  formatDate(value: string | null): string {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-GB');
  }

  private toIsoDate(d: any): string {
    // d may be a native Date or a Moment (mat-datepicker uses MatMomentDateModule app-wide, so it
    // actually produces Moment instances). Moment exposes .format(); native Date does not. Both
    // must use local time - toISOString() converts to UTC and shifts the date in BST/CEST etc.
    if (typeof d.format === 'function') {
      return d.format('YYYY-MM-DD');
    }
    const year = (d as Date).getFullYear();
    const month = ((d as Date).getMonth() + 1).toString().padStart(2, '0');
    const day = (d as Date).getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
